/**
 * Product Watch — what is happening in the categories we make, whoever is doing it.
 *
 * **Not the customer news feed.** That one watches companies we sell to and an
 * article matters because of *who* it is about. This watches the *category*, so
 * a Chili's limeade promotion, a Florida crop forecast and a competitor winning
 * a mixer account all appear with equal weight. They stay on separate screens:
 * a reader who sees them merged takes a crop report as news about a customer.
 *
 * The page is one scannable feed. **The "why it might matter" sentence sits on
 * the row, not behind a click** — that sentence is the product, not a detail.
 * It is written by a model to prompt a person, so nothing here ranks or counts
 * it, and rows that say "no angle for us" are shown rather than hidden.
 *
 * Articles with no stated publication date sort last and say so. They are never
 * dropped and never back-filled from our scan date.
 *
 * Nothing is computed here; every figure arrives in the payload.
 */

import { useState } from 'react'

import type { CustomReportProps } from '..'
import type { Cell } from '../../lib/types'
import { Card, Empty } from '../shared/Card'
import { dataset, text } from '../shared/rows'

import '../report.css'

const NONE = '—'
const TRUE = 'true'

function num(value: Cell | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function whole(value: Cell | undefined): string {
  const parsed = num(value)
  return parsed === null ? NONE : Math.round(parsed).toLocaleString('en-US')
}

function money(value: Cell | undefined): string {
  const parsed = num(value)
  return parsed === null ? NONE : `$${parsed.toFixed(4)}`
}

function Kpi({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <article className="pw-kpi">
      <p className="pw-kpi-label">{label}</p>
      <p className="pw-kpi-value cell-number">{value}</p>
      {detail !== undefined && <p className="pw-kpi-detail">{detail}</p>}
    </article>
  )
}

export function ProductWatch({ result }: CustomReportProps) {
  const headline = dataset(result, 'headline')[0]
  const articles = dataset(result, 'articles')
  const terms = dataset(result, 'terms')
  const kinds = dataset(result, 'kinds')
  const runs = dataset(result, 'runs')
  const asOf = dataset(result, 'as_of')

  const [showScope, setShowScope] = useState(false)

  const scanAge = num(headline?.scan_age_days)
  const stale = scanAge !== null && scanAge >= 42

  return (
    <div className="report-stack">
      <section className="pw-kpis">
        <Kpi
          label="Articles found"
          value={whole(headline?.articles)}
          detail="what our scan found, not what was published"
        />
        <Kpi label="Outlets" value={whole(headline?.domains)} detail="no dominant source" />
        <Kpi label="Search terms hit" value={whole(headline?.terms)} detail="of 8 we run" />
        <Kpi
          label="Newest article"
          value={text(headline?.newest) ?? NONE}
          detail="most recent stated date"
        />
        <Kpi
          label="Date not stated"
          value={whole(headline?.undated)}
          detail="shown, sorted last"
        />
        <Kpi
          label="Scan ran"
          value={scanAge === null ? NONE : `${String(scanAge)}d ago`}
          detail={stale ? 'monthly job looks missed' : 'monthly, by hand'}
        />
      </section>

      <Card
        title="What our scan found"
        subtitle="Most recent first. The second line is why it might matter to us — a model wrote it to prompt a person, so read it as a prompt, not a verdict."
        actions={
          <button
            type="button"
            className="pw-chip"
            onClick={() => {
              setShowScope(!showScope)
            }}
          >
            {showScope ? 'Hide what we search for' : 'What we search for'}
          </button>
        }
      >
        {articles.length === 0 ? (
          <Empty label="No article matches these filters. Our scan runs monthly over eight terms — an empty view is as likely to be a narrow filter as a quiet month." />
        ) : (
          <ol className="pw-feed">
            {articles.map((row, index) => {
              const url = text(row.url)
              const dated = text(row.date_stated) === TRUE
              return (
                <li className="pw-item" key={num(row.article_id) ?? String(index)}>
                  <div className="pw-item-head">
                    <h3 className="pw-title">
                      {url === null ? (
                        (text(row.title) ?? NONE)
                      ) : (
                        <a className="pw-link" href={url} target="_blank" rel="noreferrer">
                          {text(row.title) ?? NONE}
                        </a>
                      )}
                    </h3>
                    <span className={`pw-date${dated ? '' : ' is-undated'}`}>
                      {dated ? (text(row.published_at) ?? NONE) : 'date not stated'}
                    </span>
                  </div>
                  <p className="pw-why">{text(row.opportunity) ?? ''}</p>
                  <p className="pw-what">{text(row.model_summary) ?? ''}</p>
                  <div className="pw-meta">
                    <span className="pw-source">{text(row.domain) ?? NONE}</span>
                    <span className="pw-tag is-term">{text(row.term) ?? NONE}</span>
                    {(text(row.product_kinds) ?? '')
                      .split(', ')
                      .filter((kind) => kind !== '')
                      .map((kind) => (
                        <span className="pw-tag" key={kind}>
                          {kind}
                        </span>
                      ))}
                  </div>
                </li>
              )
            })}
          </ol>
        )}
      </Card>

      {showScope && (
        <Card
          title="What we search for, and why"
          subtitle="The report's own scope, in the operator's words. A term that found nothing this month is the interesting row — silence in a category we watch."
        >
          <table className="pw-table">
            <thead>
              <tr>
                <th>Term</th>
                <th className="cell-number">Found</th>
                <th>Why we watch it</th>
                <th>Angle</th>
              </tr>
            </thead>
            <tbody>
              {terms.map((row, index) => {
                const found = num(row.found) ?? 0
                return (
                  <tr key={text(row.keyword) ?? String(index)}>
                    <td className="pw-term">
                      {text(row.keyword) ?? NONE}
                      {text(row.is_active) !== TRUE && (
                        <span className="pw-tag is-off">inactive</span>
                      )}
                    </td>
                    <td className={`cell-number${found === 0 ? ' pw-zero' : ''}`}>
                      {whole(row.found)}
                    </td>
                    <td className="pw-wrap">{text(row.note) ?? NONE}</td>
                    <td className="pw-wrap pw-dim">{text(row.angle) ?? NONE}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </Card>
      )}

      <div className="pw-columns">
        <Card
          title="Product categories seen"
          subtitle="An article can touch several, so these sum past the article count — it answers “how many articles mentioned syrups”, not a split of the feed."
        >
          {kinds.length === 0 ? (
            <Empty label="No product categories in this view." />
          ) : (
            <table className="pw-table">
              <tbody>
                {kinds.map((row, index) => (
                  <tr key={text(row.kind) ?? String(index)}>
                    <td className="pw-term">{text(row.kind) ?? NONE}</td>
                    <td className="cell-number">{whole(row.found)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card
          title="Scan history"
          subtitle="Run monthly by hand. A missed run and a quiet month look identical in the row count, which is why this is on the page."
        >
          {runs.length === 0 ? (
            <Empty label="No product-watch run recorded." />
          ) : (
            <table className="pw-table">
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Status</th>
                  <th className="cell-number">Seen</th>
                  <th className="cell-number">New</th>
                  <th className="cell-number">Dup</th>
                  <th className="cell-number">Searches</th>
                  <th className="cell-number">Cost</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((row, index) => (
                  <tr key={text(row.started_at) ?? String(index)}>
                    <td>{text(row.started_at) ?? NONE}</td>
                    <td>
                      <span
                        className={`pw-tag ${text(row.run_status) === 'ok' ? 'is-ok' : 'is-off'}`}
                      >
                        {text(row.run_status) ?? NONE}
                      </span>
                    </td>
                    <td className="cell-number">{whole(row.articles_seen)}</td>
                    <td className="cell-number">{whole(row.articles_new)}</td>
                    <td className="cell-number pw-dim">{whole(row.articles_duplicate)}</td>
                    <td className="cell-number pw-dim">{whole(row.web_searches)}</td>
                    <td className="cell-number pw-dim">{money(row.est_cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      <Card title="What this is, and what it is not">
        <table className="pw-table">
          <tbody>
            {asOf.map((row, index) => (
              <tr key={text(row.source) ?? String(index)}>
                <td className="pw-field">{text(row.source) ?? NONE}</td>
                <td className="pw-asof-val">{text(row.as_of) ?? NONE}</td>
                <td className="pw-wrap">
                  <p className="pw-note">{text(row.note) ?? ''}</p>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  )
}
